/* eslint-disable jsx-a11y/alt-text */
import { useEffect, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";

import Header from "../../Header";
import Sidebar from "../../Sidebar";
import Footer from "../../Footer";
import Spinner from "../../common/Spinner";
import Build from "./Build";
import { listBuilds } from "../../../redux/actions/BuildActions";

const BuildList = ({ match }) => {
    const appId = match.params.id;
    const _organizationId = match?.params?.organizationId;
    const history = useHistory();
    const dispatch = useDispatch();

    const [currentPage, setCurrentPage] = useState(0);
    const [os, setOs] = useState("");
    const [loading, setLoading] = useState(false);
    const buildList = useSelector((state) => state.buildList);
    const role = useSelector((state) => state.userLogin.userInfo.data.role);
    const { builds, totalCount, sortBy, searchText, app } = buildList;
    const [buildsPerPage, setBuildsPerPage] = useState(20);
    const [searchTerm, setSearchTerm] = useState("");
    const organization = useSelector((state) => state?.organizationDetails?.organization);
    const userRole = organization?.user_role;
    const organizationId = organization?.organization_id;

    const runList = (overrides = {}) => {
        if (!organizationId) return;
        const args = {
            pageNum: currentPage + 1,
            buildsPerPage,
            sortBy: sortBy || "",
            searchText: searchTerm,
            filter: "",
            filterValue: "",
            ...overrides,
        };
        setLoading(true);
        dispatch(
            listBuilds(
                appId,
                args.pageNum,
                args.buildsPerPage,
                args.sortBy,
                args.searchText,
                args.filter,
                args.filterValue,
                organizationId,
            ),
        )
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        if (organizationId) {
            runList({ pageNum: 1 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, buildsPerPage, organizationId]);

    const handlePageChange = (_event, newPage) => {
        setCurrentPage(newPage);
        runList({ pageNum: newPage + 1 });
    };

    const handleRowsPerPageChange = (e) => {
        setBuildsPerPage(Number(e.target.value));
        setCurrentPage(0);
    };

    const handleFilterBy = (e) => {
        const selectedOs = e.target.value;
        setOs(selectedOs);
        setCurrentPage(0);
        runList({ filter: "OS", filterValue: selectedOs, pageNum: 1 });
    };

    const navigateToAppList = () => {
        if (match?.params?.organizationId) {
            history.push(`/organization/details/${match?.params?.organizationId}`);
        } else {
            history.push(`/apps`);
        }
    };

    return (
        <>
            <div className="container-scroller">
                <Header />
                <div className="container-fluid page-body-wrapper">
                    <Sidebar />
                    <Box component="main" className="main-panel" sx={{ display: "flex", flexDirection: "column" }}>
                        <Box sx={{ flex: 1, py: { xs: 2, md: 3 } }}>
                            <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, md: 3 } }}>
                                <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider" }}>
                                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                        <IconButton onClick={navigateToAppList} size="small" aria-label="Back">
                                            <ArrowBackIcon />
                                        </IconButton>
                                        {app?.logo_url && (
                                            <Avatar
                                                src={app.logo_url}
                                                alt={app?.name}
                                                variant="rounded"
                                                sx={{ width: 40, height: 40, border: "1px solid", borderColor: "divider" }}
                                            />
                                        )}
                                        <Typography variant="h3" component="h1" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                            {app?.name || "App"}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                        <Typography variant="h4" component="h2" sx={{ color: "text.secondary" }}>
                                            Builds
                                        </Typography>
                                    </Stack>

                                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
                                        {app?.isAndriod === "1" && app?.isIos === "1" && (
                                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                                <InputLabel id="build-os-filter">Filter by OS</InputLabel>
                                                <Select
                                                    labelId="build-os-filter"
                                                    label="Filter by OS"
                                                    value={os}
                                                    onChange={handleFilterBy}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    <MenuItem value="IOS">iOS</MenuItem>
                                                    <MenuItem value="Andriod">Android</MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}

                                        <Box sx={{ flex: 1 }} />

                                        {(userRole === "Admin" || userRole === "Member") && role !== "Super_Admin" && (
                                            <Button
                                                component={RouterLink}
                                                to={`/app/${appId}/build/add`}
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                            >
                                                New build
                                            </Button>
                                        )}
                                    </Stack>

                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 12, background: "var(--ba-bg-soft, #FAFBFC)" } }}>
                                                    <TableCell>Version</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>OS</TableCell>
                                                    <TableCell>Extension</TableCell>
                                                    <TableCell>Environment</TableCell>
                                                    <TableCell>Visibility</TableCell>
                                                    <TableCell align="center">Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {builds?.length > 0 ? (
                                                    builds.map((build) => (
                                                        <Build
                                                            build={build}
                                                            app={app}
                                                            organizationId={_organizationId}
                                                            key={build.id}
                                                        />
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 6 }}>
                                                            No builds uploaded yet.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <TablePagination
                                        component="div"
                                        count={totalCount || builds?.length || 0}
                                        page={currentPage}
                                        onPageChange={handlePageChange}
                                        rowsPerPage={buildsPerPage}
                                        onRowsPerPageChange={handleRowsPerPageChange}
                                        rowsPerPageOptions={[20, 40, 60, 80, 100]}
                                    />
                                </Paper>
                            </Container>
                        </Box>
                        <Footer />
                    </Box>
                </div>
            </div>

            {loading && <Spinner fullscreen label="Loading builds…" />}
        </>
    );
};

export default BuildList;
